import { toast } from 'react-toastify';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as API from '../../../api';
import { ApiResponse, IApiQueryOptions } from '../../../api/ApiClient';
import { SuccessToastContainer } from '../../../components/Toast/ToastContainer';
import { ConnectIntegrationType } from '../../../hooks/useConnectIntegration';
import { ApiQueryOperator, IAction, IApiList, IIntegration, IIntegrationApp, IOauthCallbackPayload, IOauthURL, IOrganizationConnection } from '../../../types';
import * as Actions from './actions';
import { selectIntegrationApps } from './selectors';
import { Integrations } from './types';

export function* getIntegrationApps() {
  try {
    // Catalog is global and immutable per session — fetch at most once and serve from cache thereafter.
    const cached: IIntegrationApp[] | null = yield select(selectIntegrationApps);
    if (cached && cached.length) {
      // Resolve to success even on a cache hit so the request always terminates in a success/fail action.
      yield put(Actions.getIntegrationAppsSuccess(cached));
      return;
    }

    // limit: 0 requests the full catalog (otherwise the API caps the response at its default page size).
    const response: ApiResponse<IApiList<IIntegrationApp>> = yield call(API.Integrations.getApps, { limit: 0 });
    yield put(Actions.getIntegrationAppsSuccess(response.body?.data ?? []));
  } catch (error: any) {
    yield put(Actions.getIntegrationAppsError(error));
  }
}

export function* listSiteIntegrations({ payload }: IAction<Integrations.LIST_SITE_INTEGRATIONS_REQUEST, { siteId: string; options?: IApiQueryOptions }>) {
  try {
    const response: ApiResponse<IApiList<IIntegration>> = yield call(API.Integrations.listSiteIntegrations, payload.siteId, payload.options);
    yield put(Actions.listSiteIntegrationsSuccess(payload.siteId, response.body?.data ?? []));
  } catch (error: any) {
    yield put(Actions.listSiteIntegrationsError(error));
  }
}

interface IIntegrationConnectionsPayload {
  integrationType: ConnectIntegrationType;
  onSuccess: (response: IOrganizationConnection[]) => void;
}

export function* getIntegrationConnections({ payload }: IAction<Integrations.GET_INTEGRATION_CONNECTIONS_REQUEST, IIntegrationConnectionsPayload>) {
  try {
    const options: IApiQueryOptions = {
      q: [{ key: 'type', value: payload.integrationType, operator: ApiQueryOperator.eq }],
    };
    const response: ApiResponse<IApiList<IOrganizationConnection>> = yield call(API.Users.showOrganizationsConnections, options);

    yield call(payload.onSuccess, response.body?.data);
    yield put(Actions.getIntegrationConnectionsSuccess(response.body?.data));
  } catch (error: any) {
    yield put(Actions.getIntegrationConnectionsError(error));
  }
}

interface IGetOauthUrlPayload extends Omit<IIntegrationConnectionsPayload, 'onSuccess'> {
  onSuccess: (response: IOauthURL) => void;
}

export function* getOauthUrl({ payload }: IAction<Integrations.GET_OAUTH_URL_REQUEST, IGetOauthUrlPayload>) {
  try {
    const response: ApiResponse<IOauthURL> = yield call(API.Integrations.showOauthUrl, payload.integrationType);

    yield call(payload.onSuccess, response.body);
    yield put(Actions.getOauthUrlSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getOauthUrlError(error, payload.integrationType));
  }
}

interface IConnectIntegrationPayload extends Omit<IIntegrationConnectionsPayload, 'onSuccess'> {
  onSuccess: () => void;
  body: IOauthCallbackPayload;
}

export function* connectIntegration({ payload }: IAction<Integrations.CONNECT_INTEGRATION_REQUEST, IConnectIntegrationPayload>) {
  try {
    yield call(API.Integrations.oauthCallback, payload.integrationType, payload.body);
    yield call(payload.onSuccess);
    yield put(Actions.connectIntegrationSuccess());
  } catch (error: any) {
    yield put(Actions.connectIntegrationError(error, payload.integrationType));
  }
}

interface IDeleteConnectionPayload extends Omit<IIntegrationConnectionsPayload, 'onSuccess'> {
  onSuccess: () => void;
  connectionId: string;
  orgId: string;
}

export function* deleteConnection({ payload }: IAction<Integrations.DELETE_CONNECTION_REQUEST, IDeleteConnectionPayload>) {
  try {
    yield call(API.Integrations.deleteConnection, payload.integrationType, payload.connectionId, payload.orgId);
    yield call(payload.onSuccess);
    yield put(Actions.deleteConnectionSuccess());
    yield call(toast, SuccessToastContainer({ message: 'Successfully removed connection' }));
  } catch (error: any) {
    yield put(Actions.deleteConnectionError(error));
  }
}

export function* watchGetIntegrationApps() {
  yield takeLatest(Integrations.GET_INTEGRATION_APPS_REQUEST, getIntegrationApps);
}

export function* watchListSiteIntegrations() {
  yield takeLatest(Integrations.LIST_SITE_INTEGRATIONS_REQUEST, listSiteIntegrations);
}

export function* watchGetIntegrationConnections() {
  yield takeEvery(Integrations.GET_INTEGRATION_CONNECTIONS_REQUEST, getIntegrationConnections);
}

export function* watchGetOauthUrl() {
  yield takeEvery(Integrations.GET_OAUTH_URL_REQUEST, getOauthUrl);
}

export function* watchConnectIntegration() {
  yield takeEvery(Integrations.CONNECT_INTEGRATION_REQUEST, connectIntegration);
}

export function* watchDeleteConnection() {
  yield takeEvery(Integrations.DELETE_CONNECTION_REQUEST, deleteConnection);
}

function* integrationsSaga() {
  yield all([fork(watchGetIntegrationApps), fork(watchListSiteIntegrations), fork(watchGetIntegrationConnections), fork(watchGetOauthUrl), fork(watchConnectIntegration), fork(watchDeleteConnection)]);
}
export { integrationsSaga };
